import { Component, Input, OnInit, OnDestroy, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';

import { Observable, Subject } from 'rxjs';
import { takeUntil, map, tap, startWith, filter } from 'rxjs/operators';

import { EnumsService, Preferences } from '@library/core/services';

@Component({
   selector: 'lib-currency-selector',
   template: `
      <ng-template #spinner>
         <mat-spinner diameter="30"></mat-spinner>
      </ng-template>

      <mat-form-field *ngIf="currencies | async as c; else spinner">
         <mat-label>Currency</mat-label>
         <mat-select [formControl]="control" [required]="required">
            <mat-option *ngIf="c.length > 10">
               <ngx-mat-select-search [formControl]="filter"
                  placeholderLabel="Search" noEntriesFoundLabel="Not Found"></ngx-mat-select-search>
            </mat-option>
            <mat-option>None</mat-option>
            <mat-option *ngFor="let currency of filtered | async" [value]="currency">{{currency}}</mat-option>
         </mat-select>
         <mat-error libInputErrors [control]="control"></mat-error>
      </mat-form-field>`,
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => CurrencySelectorComponent ),
         multi: true,
      },
   ],
})
export class CurrencySelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {

   @Input() disabled = false;
   @Input() required = false;

   currencies: Observable<string[]>;
   filtered: Observable<string[]>;

   control: FormControl = new FormControl();
   filter: FormControl = new FormControl('');

   private _value: string;

   _onDestroy: Subject<void> = new Subject();

   onChange = ( _: any ) => {};
   onTouched = ( _: any ) => {};

   get pref() { return this.prefs.getPref( 'currency' ); }

   constructor(
      public enums: EnumsService,
      private prefs: Preferences,
   ) { }

   ngOnInit() {
      this.control.valueChanges.pipe(
         takeUntil( this._onDestroy ),
         filter( value => value !== this._value ),
         tap( value => this._value = value ),
         tap( value => value ? this.prefs.upsertPref( 'currency', value ) : null ),
      ).subscribe( value => this.onChange( value ));

      this.currencies = this.enums.currencies().pipe( takeUntil( this._onDestroy ));

      this.filter.valueChanges.pipe(
         takeUntil( this._onDestroy ),
         startWith( '' ),
      ).subscribe(( value: string ) => {
         value = value.toLowerCase().trim();

         this.filtered = this.currencies.pipe(
            takeUntil( this._onDestroy ),
            map( currencies => currencies.filter( c => c.toLowerCase().includes( value ))),
         );
      });
   }

   ngOnDestroy() {
      this._onDestroy.next();
      this._onDestroy.complete();
   }

   writeValue( value: string ) {
      this.control.setValue( value );
   }

   registerOnChange( fn: ( _: any ) => void ) {
      this.onChange = fn;
   }

   registerOnTouched( fn: ( _: any ) => void ) {
      this.onTouched = fn;
   }

   setDisabledState( disabled: boolean ) {
      if ( disabled && !this.control.disabled ) {
         this.control.disable();
      } else if ( this.control.disabled ) {
         this.control.enable();
      }
   }
}
